import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import TableSection from '@sections/transactions/table';
import classNames from 'classnames/bind';
import FilterSection from '@sections/transactions/filter';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiOrderService, apiTransactionsService } from '@api';
import { TTransactionLite, TTransactionsFilter } from '@xeppt/xeppt-sdk/types/transaction';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { ENewNotificationType } from '@enum';
import { routes } from '@const/routes';
import Button from '@components/common/button';
import { useIsFirstRender } from '@mantine/hooks';
import { EUserType, TTransaction } from '@xeppt/xeppt-sdk/types';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useNavigate } from 'react-router-dom';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';
import Typography from '@components/common/typography';
import { useDebounceValue } from 'usehooks-ts';
import { Icon, TIconType } from '@components/icons';
import Info from '@components/common/info';
import { useUserContext } from '@hooks/context/useUserContext';
import { formatToDateString } from '@utils/date';
import { prettifyAmount } from '@utils/index';

const cx = classNames.bind(styles);

interface IProps {
    layoutType: 'sales' | 'payments' | 'igaming';
}

const PaymentsLayout: FC<IProps> = ({ layoutType }) => {
    const igamingDefaultFrom = useMemo(() => {
        if (layoutType !== 'igaming') return null;
        return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    }, []);
    const igamingDefaultTo = useMemo(() => {
        if (layoutType !== 'igaming') return null;
        return new Date();
    }, []);
    const [filter, setFilter] = useState<TTransactionsFilter>(() => ({
        action: undefined,
        date_from: igamingDefaultFrom ?? undefined,
        date_to: igamingDefaultTo ?? undefined
    }));
    const { user } = useUserContext();
    const navigate = useNavigate();
    const [id] = useQueryParams(['id', 'intent']);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounceValue(search, 300);
    const { onOpen, changeModalData } = useModalContext();
    const isFirstRender = useIsFirstRender();
    const [page, setPage] = useState(1);
    const loader = useRef(null);
    const [transactions, setTransactions] = useState<TTransactionLite[]>([]);
    const [trigger, setTrigger] = useState(0);
    const { handleBack } = useNavigationContext();
    const [triggerClearFilter, setTriggerClearFilters] = useState(0);
    const { submitLocale } = useLocales();
    const [isOpen, setIsOpen] = useState(false);
    const {
        data: transactionsResponse,
        isLoading: isTransactionsLoading,
        handleRequest: handleGetTransactions
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiOrderService.findBusinessSales({
                date_to: request?.date_to,
                date_from: request?.date_from,
                search: request?.search,
                type: request?.type?.length === 0 ? undefined : request?.type,
                status: request?.status?.length === 0 ? undefined : request?.status,
                page_size: 30,
                page,
                order: request?.order ?? 'DESC'
            });
        },
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setTransactions((state) => [...state, ...data.data]);
            } else {
                setTransactions(data?.data || []);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION,
        deps: [layoutType, user],
        condition: layoutType !== 'igaming' && user?.type !== EUserType.INDIVIDUAL
    });

    const {
        data: gamingResponse,
        isLoading: isGamingLoading,
        handleRequest: handleGetGamingTransactions
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiOrderService.findIndividualSales({
                date_to: request?.date_to,
                date_from: request?.date_from,
                search: request?.search,
                type: request?.type?.length === 0 ? undefined : request?.type,
                page_size: 30,
                page,
                order: 'DESC'
            });
        },
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setTransactions((state) => [...state, ...data.data]);
            } else {
                setTransactions(data?.data || []);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION,
        deps: [layoutType, user],
        condition: layoutType === 'igaming' && user?.type === EUserType.INDIVIDUAL
    });

    // Fires the initial load for SPA nav, where `layoutType` and `user` are
    // already resolved by the time this page mounts: the deps:[layoutType, user]
    // effects inside the two useApiQuery calls above only fetch on their SECOND
    // run (isInitialRequest flips true after the first), which never comes if
    // neither value changes again post-mount. On a full reload, `user` resolves
    // async after mount, so those effects' own deps change fires the fetch
    // instead. The empty deps array here means this runs exactly once, so the
    // two paths never both fire for the same mount. Guarded on the same
    // condition each query already uses, so it never fetches for the wrong
    // wallet/layout combination.
    useEffect(() => {
        if (!user) return;
        if (layoutType !== 'igaming' && user.type !== EUserType.INDIVIDUAL) {
            setPage(1);
            setTransactions([]);
            handleGetTransactions({ ...filter, isNew: true });
        } else if (layoutType === 'igaming' && user.type === EUserType.INDIVIDUAL) {
            setPage(1);
            setTransactions([]);
            handleGetGamingTransactions({ ...filter, isNew: true });
        }
    }, []);

    useEffect(() => {
        if (debouncedSearch.length >= 3) {
            if (layoutType === 'igaming') {
                handleGetGamingTransactions({ ...filter, search: debouncedSearch, isNew: true });
            } else {
                handleGetTransactions({ ...filter, search: debouncedSearch, isNew: true });
            }
        }
    }, [debouncedSearch, layoutType]);

    useEffect(() => {
        const loadItems = () => {
            if (
                layoutType === 'igaming' &&
                transactions.length < (gamingResponse?.total || 0) &&
                !isGamingLoading
            ) {
                handleGetGamingTransactions();
            } else if (
                layoutType !== 'igaming' &&
                transactions.length < (transactionsResponse?.total || 0) &&
                !isTransactionsLoading
            ) {
                handleGetTransactions();
            }
        };

        loadItems();
    }, [trigger]);

    const handleObserver = useCallback((entries: any) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setTrigger((prev) => prev + 1);
        }
    }, []);

    useEffect(() => {
        if (!isFirstRender) {
            if (layoutType === 'igaming') {
                handleGetGamingTransactions({ ...filter, search: debouncedSearch, isNew: true });
            } else {
                handleGetTransactions({ ...filter, search: debouncedSearch, isNew: true });
            }
        }
    }, [layoutType]);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const handleViewTransaction = (transaction: TTransaction) => {
        changeModalData({
            transaction,
            refetchTransactions: () => handleGetTransactions({ ...filter, isNew: true })
        });
        onOpen({
            modalId: modalIds.VIEW_TRANSACTION,
            onClose: () => {
                if (id) {
                    navigate(routes.transactions);
                }
            }
        });
    };

    useApiQuery({
        method: () => apiTransactionsService.getTransactionById(id || ''),
        condition: !!id,
        deps: [id],
        onSuccess: (data) => {
            handleViewTransaction(data);
        }
    });

    const renderData: {
        title: string;
        totals: {
            title: string;
            helper: string;
            value: string;
            style: string;
            onClick?: () => void;
            icon: TIconType;
        }[];
    } = useMemo(() => {
        switch (layoutType) {
            case 'igaming':
                return {
                    title: 'Payments',
                    totals: [
                        {
                            title: 'Available balance',
                            helper: 'The amount from completed deposits available for withdrawal to your XEPPT wallet',
                            value: `$${(gamingResponse?.total_available_balance || 0).toFixed(2)}`,
                            style: styles.green,
                            onClick: () => {},
                            icon: 'money'
                        },
                        {
                            title: 'Pending amount',
                            helper: 'The amount of pending deposits that are still being processed and aren’t available for withdrawal yet',
                            value: `$${(gamingResponse?.total_pending_amount || 0).toFixed(2)}`,
                            style: styles.total,
                            icon: 'clock'
                        },
                        {
                            title: 'Total withdrawn',
                            helper: 'Total amount you’ve withdrawn to your XEPPT wallet',
                            value: `$${(gamingResponse?.total_withdrawn_amount || 0).toFixed(2)}`,
                            style: styles.pending,
                            icon: 'outlined_wallet'
                        }
                    ]
                };
            case 'payments':
                return {
                    title: 'Payments',
                    totals: [
                        {
                            title: 'Total received',
                            helper: 'The total amount of successful customer purchases in the selected period',
                            value: `$${prettifyAmount((transactionsResponse?.total_received || 0).toFixed(2))}`,
                            style: styles.green,
                            icon: 'money'
                        },
                        {
                            title: 'Successful purchases',
                            helper: 'The number of successful customer purchases in the selected period',
                            value: `${transactionsResponse?.total_approved_count ?? 0}`,
                            style: styles.total,
                            icon: 'check_outlined'
                        },
                        {
                            title: 'Failed purchases',
                            helper: 'The number of failed customer purchases in the selected period',
                            value: `${transactionsResponse?.total_failed || 0}`,
                            style: styles.pending,
                            icon: 'error_outlined'
                        }
                    ]
                };
            case 'sales':
                return {
                    title: 'Sales',
                    totals: [
                        {
                            title: 'Total received',
                            helper: 'The total amount of successful customer purchases in the selected period',
                            value: `$${prettifyAmount((transactionsResponse?.total_received || 0).toFixed(2))}`,
                            style: styles.total,
                            icon: 'money'
                        },
                        {
                            title: 'Successful purchases',
                            helper: 'The number of successful customer purchases in the selected period',
                            value: `${transactionsResponse?.total_approved_count ?? 0}`,
                            style: styles.green,
                            icon: 'check_outlined'
                        },
                        {
                            title: 'Failed purchases',
                            helper: 'The number of failed customer purchases in the selected period',
                            value: `${transactionsResponse?.total_failed || 0}`,
                            style: styles.failed,
                            icon: 'error_outlined'
                        }
                    ]
                };
        }
    }, [layoutType, transactionsResponse, gamingResponse]);

    return (
        <motion.div {...pageAnimation}>
            <div className={styles.wrapper}>
                <div className={cx([styles.left, { isOpen }])}>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => handleBack(routes.dashboard)}>
                            {submitLocale('back')}
                        </Button>
                        <Typography variant="h4">{renderData.title}</Typography>
                    </div>
                    <div className={styles.points_wrapper_wrapper}>
                        <div className={styles.points_wrapper}>
                            {renderData.totals.map((item) => (
                                <div
                                    key={item.style}
                                    className={`${styles.point_wrapper} ${item.style}`}>
                                    <div className={styles.icon}>
                                        <Icon name={item.icon} />
                                    </div>
                                    <div className={styles.text_wrapper}>
                                        <div className={styles.title}>
                                            <Typography variant="body4">{item.title}</Typography>
                                            <Info variant="left" content={<>{item.helper}</>} />
                                        </div>
                                        <Typography variant="h3" className={styles.amount}>
                                            {item.value}
                                        </Typography>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <TableSection
                        search={search}
                        isSale
                        onSearch={(val) => setSearch(val)}
                        triggerClearFilter={() => setTriggerClearFilters((prev) => prev + 1)}
                        transactions={transactions}
                        toggleFilter={() => setIsOpen((state) => !state)}
                        isTransactionsLoading={isTransactionsLoading}
                        filter={filter}
                        showDownload={layoutType === 'sales'}
                        refetchTransactions={() => {
                            if (layoutType === 'igaming') {
                                handleGetGamingTransactions({ ...filter, isNew: true });
                            } else {
                                handleGetTransactions({ ...filter, isNew: true });
                            }
                        }}
                        onChangeFilter={(value) => {
                            setPage(1);
                            setTransactions([]);
                            setFilter(value);
                            if (layoutType === 'igaming') {
                                handleGetGamingTransactions({ ...value, isNew: true });
                            } else {
                                handleGetTransactions({ ...value, isNew: true });
                            }
                        }}
                    />
                    <div ref={loader} />
                </div>
                {isOpen && <div className={styles.overlay} onClick={() => setIsOpen(false)} />}
                <div className={cx([styles.right, { isOpen }])}>
                    <FilterSection
                        toggleFilter={() => setIsOpen((state) => !state)}
                        triggerClearFilter={triggerClearFilter}
                        activeTab={'transactions'}
                        isSale
                        initialDateFrom={
                            igamingDefaultFrom
                                ? formatToDateString(igamingDefaultFrom, '00:00:00')
                                : null
                        }
                        initialDateTo={
                            igamingDefaultTo
                                ? formatToDateString(igamingDefaultTo, '23:59:59')
                                : null
                        }
                        onChangeFilter={(value) => {
                            setPage(1);
                            setTransactions([]);
                            setFilter(value);
                            if (layoutType === 'igaming') {
                                handleGetGamingTransactions({ ...value, isNew: true });
                            } else {
                                handleGetTransactions({ ...value, isNew: true });
                            }
                        }}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default PaymentsLayout;
